import {ConvertBoardViewSummaryToValues, ItemSummaryVariablesType, parsedDateValue} from 'global';
import {evaluate} from 'mathjs';
import hash from 'object-hash';
import {
    DataType,
    FieldConstantsVariables,
    FieldTypeEnum,
    ItemDataFieldModel,
    ItemDataResponse,
    ItemDataUpdateRequest,
} from 'models/board';
import {useCallback} from 'react';

export const useItemDataHelper = () => {
    const getItemDataSummaryVariablesList = useCallback((item: ItemDataResponse): ItemSummaryVariablesType => {
        const itemSummaryVariablesList: ItemSummaryVariablesType = {};

        for (const x of item.data) {
            try {
                if (x.dataType === DataType.SelectOption) {
                    if (x.isMultiValue && x.value.multiSelectedOptions && x.value.multiSelectedOptions.length > 0) {
                        itemSummaryVariablesList[`${x.uniqueId}`] = x.value.multiSelectedOptions;
                    } else {
                        itemSummaryVariablesList[`${x.uniqueId}`] = x.value.selectedOption;
                    }
                } else if (x.dataType === DataType.SelectOptionWithColor) {
                    if (x.isMultiValue && x.value.multiSelectedOptions && x.value.multiSelectedOptions.length > 0) {
                        itemSummaryVariablesList[`${x.uniqueId}`] = x.value.multiSelectedOptions;
                    } else {
                        itemSummaryVariablesList[`${x.uniqueId}`] = x.value.selectedOption;
                    }
                } else if (x.dataType === DataType.Integer || x.dataType === DataType.Double) {
                    itemSummaryVariablesList[`${x.uniqueId}`] = x.value.integerValue?.toString() ?? '';
                } else if (x.dataType === DataType.Date || x.dataType === DataType.DateTime) {
                    itemSummaryVariablesList[`${x.uniqueId}`] = parsedDateValue(x.value.value ?? '');
                } else {
                    itemSummaryVariablesList[`${x.uniqueId}`] = x.value.value ?? '';
                }
            } catch (error) {
                console.error('Error parsing select option', error);
            }
        }

        itemSummaryVariablesList[FieldConstantsVariables.TicketNo.toString()] = item.ticketNo.toString();
        itemSummaryVariablesList[FieldConstantsVariables.timeTrackerCounter.toString()] = '{{timeTrackerCounter}}';

        return itemSummaryVariablesList;
    }, []);

    const getItemDataValueWithFormulaApplied = useCallback(
        (field: ItemDataFieldModel, itemSummaryVariables: ItemSummaryVariablesType[]): string | undefined => {
            if (field.formula && field.formula !== '') {
                //replace all variables with their values
                const theEvaluativeValue = ConvertBoardViewSummaryToValues(
                    itemSummaryVariables ?? [],
                    field.formula ?? '',
                );

                try {
                    let evaluatedValue = evaluate(theEvaluativeValue);

                    if (field.dataType === DataType.Integer) {
                        evaluatedValue = Math.floor(evaluatedValue);
                    } else if (field.dataType === DataType.Double) {
                        evaluatedValue = Math.round(evaluatedValue * 10) / 10;
                    }
                    return evaluatedValue.toString();
                } catch (e) {
                    console.log('ConvertBoardViewSummaryToValues errore', e);
                }
            }
        },
        [],
    );
    const checkTheDifferent = useCallback((first: ItemDataUpdateRequest, second: ItemDataUpdateRequest) => {
        const FirstKeys = flattenAllKeysWithArrayInfo({
            value: first.data
                .filter(
                    x =>
                        x.fieldType !== FieldTypeEnum.TimeTrackerCounter.toString() &&
                        x.fieldType !== FieldTypeEnum.UploadFile.toString(),
                )
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .flatMap(x => x.value),
            itemStatusId: first.itemStatusId,
            sprintId: first.sprintId,
        });
        const SecondKeys = flattenAllKeysWithArrayInfo({
            value: second.data
                .filter(
                    x =>
                        x.fieldType !== FieldTypeEnum.TimeTrackerCounter.toString() &&
                        x.fieldType !== FieldTypeEnum.UploadFile.toString(),
                )
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .flatMap(x => x.value),
            itemStatusId: second.itemStatusId,
            sprintId: second.sprintId,
        });

        return hash(FirstKeys) !== hash(SecondKeys);
    }, []);
    function flattenAllKeysWithArrayInfo(input: any): Record<string, any> {
        const result: Record<string, any> = {};

        function isEmpty(value: any): boolean {
            return (
                value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)
            );
        }

        function helper(obj: any, path = '') {
            if (Array.isArray(obj)) {
                const len = obj.length;
                obj.forEach((item, index) => {
                    helper(item, `${path}_len${len}_${index}`);
                });
            } else if (typeof obj === 'object' && obj !== null) {
                for (const [key, value] of Object.entries(obj)) {
                    if (isEmpty(value)) continue;

                    if (typeof value === 'object') {
                        helper(value, path ? `${path}_${key}` : key);
                    } else {
                        const finalKey = path ? `${key}_${path}` : key;
                        result[finalKey] = value;
                    }
                }
            }
        }

        helper(input);
        return result;
    }

    return {getItemDataSummaryVariablesList, getItemDataValueWithFormulaApplied, checkTheDifferent};
};
